import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';

/**
 * Manages the current user's role within the organization.
 *
 * Role hierarchy (lowest to highest):
 *   collector (0) < banker (1) < admin (2) = owner (2)
 *
 * Usage:
 *   await roleService.loadRole(uid, firestore);
 *   if (roleService.hasMinRole('banker')) { ... }
 */
@Injectable({ providedIn: 'root' })
export class RoleService {
  private currentRole: string = 'collector'; // default to lowest
  private currentUid: string = '';

  private roleLevel: Record<string, number> = {
    collector: 0,
    banker: 1,
    admin: 2,
    owner: 2,
  };

  /**
   * Loads the user's role from the OrganizationUsers collection.
   * Falls back to 'collector' if the document or Role field is missing.
   */
  async loadRole(uid: string, firestore: AngularFirestore): Promise<string> {
    this.currentUid = uid;
    const doc = await firestore.doc<any>(`OrganizationUsers/${uid}`).get().toPromise();
    const raw = doc?.data()?.Role;
    this.currentRole = raw ? raw.toLowerCase() : 'collector';
    return this.currentRole;
  }

  /** Directly set the role (e.g. from an already-loaded OrgUser). */
  setRole(role: string, uid: string): void {
    this.currentUid = uid;
    this.currentRole = role ? role.toLowerCase() : 'collector';
  }

  get role(): string { return this.currentRole; }
  get uid(): string { return this.currentUid; }

  /** Returns true if the current role meets or exceeds the given minimum role. */
  hasMinRole(minRole: string): boolean {
    return (this.roleLevel[this.currentRole] ?? 0) >= (this.roleLevel[minRole] ?? 0);
  }

  isCollector(): boolean { return this.currentRole === 'collector'; }
  isBanker(): boolean { return this.hasMinRole('banker'); }
  isAdmin(): boolean { return this.hasMinRole('admin'); }
}
